/**
 * Working a value out, without writing a formula.
 *
 * The designer could always *bind* a property to a field and never work one out,
 * so every calculation in a document arrived in a template somebody had written by
 * hand. A host with the expression editor off could read its own invoice total and
 * not change it; a host with it on asked a business user to type
 * `sum(invoice.lineItems.amount) * (1 + taxRate)`. This is the same feature missing
 * twice, and it is the same answer both times — so this is not a Cloud component:
 * it is how the designer builds arithmetic, and the expression box beside it is the
 * escape hatch for the people who want one.
 *
 * ## It reads as a sentence
 *
 * A starting value and then steps, each applied to everything before it:
 *
 *     Total of Lines › Amount
 *       then add VAT rate as a percentage
 *
 * No brackets, because an interface with brackets in it is a formula editor. The
 * model in `@broadpaper/core` does the bracketing, and `ConditionBuilder` beside
 * this file is the precedent: the same trade, for conditions, accepted years ago.
 *
 * ## It never rewrites what it cannot rebuild
 *
 * `fromExpression` refuses anything it would re-emit differently, and a refusal
 * means the caller shows the value read-only rather than offering this. So opening
 * this panel on an existing template and closing it again cannot change a document,
 * which is the property that makes it safe to put in front of somebody whose
 * invoices are already going out.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  createSchemaScope,
  fromExpression,
  labelForPath,
  toExpression,
  type Calculation,
  type CalcAggregate,
  type CalcOp,
  type CalcTerm,
  type DataSource,
  type FieldSchema,
  type FieldType
} from "@broadpaper/core";
import { Button, NumberInput, Select } from "../../ui/primitives.js";
import { Icon } from "../../ui/icons.js";
import { Popover } from "../../ui/floating.js";
import { flattenSchemas, type PickerEntry } from "../DataPicker.js";

/** What arithmetic can be done to, and what a step's term may be. */
const NUMERIC: FieldType[] = ["number", "currency", "percent"];

/**
 * The operators, in the words somebody would use.
 *
 * "add as a percentage" rather than "multiply by one plus", because the second is
 * the same arithmetic and requires the user to have derived it. It is what makes
 * VAT, a discount and a retention one choice each instead of three.
 */
const OPS: Array<{ value: CalcOp; label: string }> = [
  { value: "add", label: "add" },
  { value: "subtract", label: "subtract" },
  { value: "multiply", label: "multiply by" },
  { value: "divide", label: "divide by" },
  { value: "addPercent", label: "add, as a percentage" },
  { value: "subtractPercent", label: "subtract, as a percentage" }
];

const AGGREGATES: Array<{ value: CalcAggregate; label: string; of: string }> = [
  { value: "sum", label: "Total of", of: "added up across every row" },
  { value: "avg", label: "Average of", of: "the mean across every row" },
  { value: "count", label: "How many", of: "how many rows there are" },
  { value: "max", label: "Highest", of: "the largest in any row" },
  { value: "min", label: "Lowest", of: "the smallest in any row" }
];

/** A fresh calculation, for a property that had nothing in it. */
const EMPTY: Calculation = { start: { kind: "field", path: "" }, steps: [] };

/**
 * Whether every term has been chosen yet.
 *
 * Half-built is the normal state of this panel — somebody adds a step before
 * deciding what goes in it — and a half-built calculation must not be written to
 * the template, because `sum(x) * (1 + coalesce(, 0))` does not parse. The first
 * version of this committed on every keystroke and read its own state back out of
 * the value, so the moment a term was incomplete the expression became
 * unparseable, `fromExpression` returned null, and the builder reset to empty
 * underneath the user's hands.
 */
function complete(calc: Calculation): boolean {
  const ok = (term: CalcTerm) => term.kind === "number" || term.path.trim() !== "";
  return ok(calc.start) && calc.steps.every((s) => ok(s.term));
}

export interface CalculationBuilderProps {
  value: string;
  onChange: (expr: string) => void;
  dataSources: DataSource[];
  scopeVars?: Record<string, FieldSchema>;
  /** Format choices offered for the result, as `{ label, pipe }`. */
  formats?: Array<{ label: string; pipe: string }>;
}

export function CalculationBuilder({ value, onChange, dataSources, scopeVars, formats = [] }: CalculationBuilderProps) {
  const entries = useMemo(() => flattenSchemas(dataSources, scopeVars), [dataSources, scopeVars]);
  const scope = useMemo(() => createSchemaScope(dataSources).child(scopeVars ?? {}), [dataSources, scopeVars]);

  /*
   * The calculation being edited is held here, not derived from the value.
   *
   * It has to be: a half-chosen step is not an expression, so there is nothing to
   * derive it from. The value is still the source of truth for what the *document*
   * says — `mine` is what this panel last wrote, so a change arriving from
   * anywhere else (an undo, another panel, a template reloaded) resyncs and a
   * change of our own does not throw away the step somebody is in the middle of.
   */
  const [calc, setCalc] = useState<Calculation>(() => fromExpression(value) ?? EMPTY);
  const mine = useRef<string | null>(null);
  useEffect(() => {
    if (value !== mine.current) setCalc(fromExpression(value) ?? EMPTY);
  }, [value]);

  const commit = (next: Calculation) => {
    setCalc(next);
    // Nothing is written until it means something. The panel shows the
    // half-finished step; the document keeps the value it had.
    if (!complete(next)) return;
    const expr = toExpression(next);
    mine.current = expr;
    onChange(expr);
  };
  const naming = (term: CalcTerm) => termLabel(term, entries, scope);

  return (
    <div className="bpe-calc">
      {/*
        A step is a labelled block rather than a row of controls.
        
        Three selects and a field name across one line is what made this
        unreadable in a popover: every control shrank to its truncation point —
        "a total", "Total of", "In…" — and the sentence the builder is supposed to
        read as could not be recovered from the abbreviations.
      */}
      <div className="bpe-calc__step">
        <span className="bpe-calc__lead">Start with</span>
        <TermField term={calc.start} entries={entries} scope={scope} onChange={(start) => commit({ ...calc, start })} />
      </div>

      {calc.steps.map((step, i) => (
        <div className="bpe-calc__step" key={i}>
          <div className="bpe-calc__row">
            <span className="bpe-calc__lead">then</span>
            <div className="bpe-grow" />
            <Button
              size="sm"
              variant="ghost"
              icon="x"
              tooltip="Remove this step"
              onClick={() => commit({ ...calc, steps: calc.steps.filter((_, j) => j !== i) })}
            />
          </div>
          <Select
            value={step.op}
            onChange={(op) => commit({ ...calc, steps: calc.steps.map((s, j) => (i === j ? { ...s, op: op as CalcOp } : s)) })}
            options={OPS}
          />
          <TermField
            term={step.term}
            entries={entries}
            scope={scope}
            onChange={(term) => commit({ ...calc, steps: calc.steps.map((s, j) => (i === j ? { ...s, term } : s)) })}
          />
        </div>
      ))}

      <div className="bpe-calc__row">
        <Button
          size="sm"
          icon="plus"
          onClick={() => commit({ ...calc, steps: [...calc.steps, { op: "add", term: { kind: "field", path: "" } }] })}
        >
          Add a step
        </Button>
      </div>

      {formats.length ? (
        <div className="bpe-calc__row">
          <span className="bpe-calc__lead">Shown as</span>
          <Select
            value={calc.format ?? ""}
            onChange={(pipe) => commit({ ...calc, ...(pipe ? { format: pipe } : { format: undefined }) })}
            options={[{ value: "", label: "As it comes" }, ...formats.map((f) => ({ value: f.pipe, label: f.label }))]}
          />
        </div>
      ) : null}

      {/*
        The sentence, last. It is the check a person actually performs before
        trusting this — "total of amount, add VAT rate as a percentage" is
        something you can be sure about at a glance, and a bracketed expression
        is not.
      */}
      <p className="bpe-calc__reads">
        {naming(calc.start)}
        {calc.steps.map((step, i) => (
          <span key={i}>
            , {OPS.find((o) => o.value === step.op)?.label} {naming(step.term)}
          </span>
        ))}
      </p>
    </div>
  );
}

/* --------------------------------- one term -------------------------------- */

function TermField({
  term,
  entries,
  scope,
  onChange
}: {
  term: CalcTerm;
  entries: PickerEntry[];
  scope: ReturnType<typeof createSchemaScope>;
  onChange: (term: CalcTerm) => void;
}) {
  return (
    <div className="bpe-calc__term">
      <Select
        value={term.kind}
        onChange={(kind) => onChange(blankTerm(kind as CalcTerm["kind"], term))}
        options={[
          { value: "field", label: "a field" },
          { value: "aggregate", label: "a total" },
          { value: "number", label: "a number" }
        ]}
      />

      {term.kind === "number" ? (
        <NumberInput value={term.value} onChange={(v) => onChange({ kind: "number", value: v ?? 0 })} placeholder="0" />
      ) : null}

      {term.kind === "aggregate" ? (
        <Select
          value={term.fn}
          onChange={(fn) => onChange({ ...term, fn: fn as CalcAggregate })}
          options={AGGREGATES.map((a) => ({ value: a.value, label: a.label }))}
        />
      ) : null}

      {term.kind !== "number" ? (
        <Popover
          className="bpe-picker"
          trigger={
            <button type="button" className={`bpe-binding__btn ${term.path ? "" : "bpe-binding__btn--empty"}`}>
              <Icon name={term.kind === "aggregate" ? "sigma" : "braces"} />
              <span className="bpe-truncate">{term.path ? pathLabel(term.path, scope) : term.kind === "aggregate" ? "Choose a column" : "Choose a field"}</span>
            </button>
          }
        >
          {(close) => (
            <TermPicker
              entries={entries}
              wants={term.kind}
              /* `count` is the one aggregate that works on anything: how many rows
                 there are does not depend on what is in them. */
              numericOnly={term.kind === "aggregate" ? term.fn !== "count" : true}
              onPick={(path) => {
                onChange({ ...term, path });
                close();
              }}
            />
          )}
        </Popover>
      ) : null}
    </div>
  );
}

/** Changing what kind of term this is keeps the path where it still means something. */
function blankTerm(kind: CalcTerm["kind"], from: CalcTerm): CalcTerm {
  const path = from.kind === "number" ? "" : from.path;
  if (kind === "number") return { kind: "number", value: 0 };
  if (kind === "aggregate") return { kind: "aggregate", fn: "sum", path };
  return { kind: "field", path };
}

/**
 * The fields a term may be.
 *
 * A plain field term wants a single value; an aggregate wants a *column* of a
 * list, which `flattenSchemas` already exposes as an `array`-typed entry for
 * exactly this purpose — `invoice.lineItems.amount` reading "Lines › each Amount".
 */
export function termOptions(entries: PickerEntry[], wants: "field" | "aggregate", numericOnly: boolean): PickerEntry[] {
  return entries.filter((e) => {
    if (wants === "aggregate") {
      // A list, and — for everything but `count` — a list of numbers. Offering a
      // column of descriptions to add up is offering a wrong answer confidently.
      if (e.type !== "array") return false;
      const item = e.schema.itemSchema;
      return numericOnly ? Boolean(item && NUMERIC.includes(item.type)) : true;
    }
    return numericOnly ? NUMERIC.includes(e.type) : e.type !== "array" && e.type !== "object";
  });
}

export function TermPicker({
  entries,
  wants,
  numericOnly,
  onPick
}: {
  entries: PickerEntry[];
  wants: "field" | "aggregate";
  numericOnly: boolean;
  onPick: (path: string) => void;
}) {
  const shown = termOptions(entries, wants, numericOnly);

  const groups = new Map<string, PickerEntry[]>();
  for (const e of shown) groups.set(e.group, [...(groups.get(e.group) ?? []), e]);

  return (
    <div>
      <div className="bpe-picker__list" role="listbox">
        {!shown.length ? (
          <div className="bpe-empty">
            {wants === "aggregate" ? "No lists of numbers in your data to total up." : "No numbers in your data to use here."}
          </div>
        ) : null}
        {[...groups].map(([group, items]) => (
          <div key={group}>
            <div className="bpe-picker__group">{group}</div>
            {items.map((e) => (
              <button key={e.expr} type="button" role="option" className="bpe-data-field" onClick={() => onPick(e.expr)}>
                <Icon name={wants === "aggregate" ? "list-tree" : "hash"} className="bpe-data-field__icon" />
                <span className="bpe-truncate">{e.label}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* --------------------------------- naming --------------------------------- */

function pathLabel(path: string, scope: ReturnType<typeof createSchemaScope>): string {
  return labelForPath(
    path.split(".").map((name) => ({ kind: "prop" as const, name })),
    scope
  );
}

/** One term, in words. Used for the sentence under the builder. */
function termLabel(term: CalcTerm, entries: PickerEntry[], scope: ReturnType<typeof createSchemaScope>): string {
  if (term.kind === "number") return String(term.value);
  if (!term.path) return "…";
  const named = entries.find((e) => e.expr === term.path)?.label ?? pathLabel(term.path, scope);
  if (term.kind === "field") return named;
  return `${AGGREGATES.find((a) => a.value === term.fn)?.label ?? "Total of"} ${named}`;
}

/** Whether a property is worth offering this for at all. */
export function canCalculate(types: FieldType[] | undefined): boolean {
  return !types || types.some((t) => NUMERIC.includes(t));
}
